import React, { useEffect } from 'react';

/**
 * Origen público de la landing (issue #293). El dominio antiguo tumesaya.com
 * sigue registrado, pero sólo responde un 301 hacia este subdominio.
 */
const ORIGIN = 'https://tumesaya.centraldereservas.ai';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  canonical?: string;
  robots?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'TuMesaYa - Gestión de Reservas para Restaurantes',
  description = 'Optimiza la gestión de reservas de tu restaurante con TuMesaYa. Sistema automático de reservas, gestión de mesas y mejora de la experiencia del cliente.',
  keywords = 'restaurante, reservas, gestión, mesas, TuMesaYa, automatización, clientes',
  image = `${ORIGIN}/og.png`,
  url = ORIGIN,
  type = 'website',
  author = 'TuMesaYa',
  canonical,
  robots = 'index,follow',
}) => {
  const fullUrl = canonical || url;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string) => {
      let meta = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.name = name;
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updatePropertyMetaTag = (property: string, content: string) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    const updateCanonical = (href: string) => {
      let link = document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    updateMetaTag('author', author);
    updateMetaTag('robots', robots);
    updateMetaTag('language', 'Spanish');
    updateMetaTag('revisit-after', '7 days');
    updateCanonical(fullUrl);

    // Open Graph tags
    updatePropertyMetaTag('og:type', type);
    updatePropertyMetaTag('og:url', fullUrl);
    updatePropertyMetaTag('og:title', title);
    updatePropertyMetaTag('og:description', description);
    updatePropertyMetaTag('og:image', image);
    updatePropertyMetaTag('og:site_name', 'TuMesaYa');
    updatePropertyMetaTag('og:locale', 'es_ES');

    // Twitter tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:url', fullUrl);
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', image);

    // Mobile meta tags
    updateMetaTag('theme-color', '#FFCE00');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default');
    updateMetaTag('apple-mobile-web-app-title', 'TuMesaYa');

    // Add structured data
    const addStructuredData = (id: string, data: object) => {
      let script = document.getElementById(id) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        document.head.appendChild(script);
      }
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(data);
    };

    // Organization structured data
    addStructuredData('seo-organization-schema', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'TuMesaYa',
      url: ORIGIN,
      logo: `${ORIGIN}/favicon.svg`,
      description: 'Sistema de gestión de reservas para restaurantes',
      address: {
        '@type': 'PostalAddress',
        addressCountry: 'ES',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        availableLanguage: 'Spanish',
      },
    });

    // Service structured data
    addStructuredData('seo-service-schema', {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Gestión de Reservas para Restaurantes',
      provider: {
        '@type': 'Organization',
        name: 'TuMesaYa',
      },
      description:
        'Sistema automatizado de gestión de reservas y mesas para restaurantes',
      areaServed: 'Spain',
      serviceType: 'Software as a Service',
    });
  }, [
    title,
    description,
    keywords,
    author,
    image,
    url,
    type,
    canonical,
    robots,
    fullUrl,
  ]);

  return null;
};
